import React, { useCallback, useEffect, useMemo, useState } from "react";

import { AudioMessagePlayer } from "@korben/audio-player";
import {
  messageLabelsType,
  messageVariantsType
} from "@/shared/constants/messages";
import { ATTACHMENT_STATUS } from "@/features/chat/shared/types/media/media";
import {
  Icon,
  MediaSlider,
  MessageBubble,
  MessageRequestActions,
  Text
} from "@orgmym/leeloo-core";
import type { MediaSliderItem } from "@orgmym/leeloo-core";
import clsx from "clsx";
import ReactPlayer from "react-player";
import { useTranslation } from "react-i18next";

import { hlsPlayerConfig } from "@/utils/reactPlayerConfig";
import { useCloudFrontCookies } from "@/utils/useCloudFrontCookies";

import {
  MediaOfferCard,
  type MediaOfferImage
} from "@/features/chat/shared/components/media-offer-card";
import * as mediaOfferCardStyles from "@/features/chat/shared/components/media-offer-card/media-offer-card.css";
import { MentionText } from "@/features/chat/shared/components/mention-text";
import { Skeleton } from "@/features/chat/shared/components/skeleton";
import { useIsMobile } from "@/features/chat/shared/hooks";
import { useMessageListScroll } from "@/features/chat/shared/hooks";
import type { ChatMessage } from "@/features/chat/shared/types";
import {
  formatMessageDateSeparator,
  formatMessageTime
} from "@/features/chat/shared/utils/date.utils";
import {
  VIDEO_TYPES,
  buildLiveOffer,
  buildMediaOffer,
  getMessageStatus,
  getReadStatus
} from "@/features/chat/shared/utils/message.utils";
import { replaceEmojiPlaceholders } from "@/features/chat/shared/utils/emoji.utils";

import * as styles from "./message-list.css";

const AUDIO_PLAYER_LABELS = {
  play: "Play voice message",
  pause: "Pause voice message",
  loading: "Loading voice message",
  error: "Voice message unavailable",
  retry: "Retry",
  seek: "Seek voice message",
  speed: "Change playback speed",
  unread: "Unread voice message",
  sent: "Sent",
  read: "Read"
};

const AUDIO_STATUS_ICON_SIZE = { width: "14px", height: "14px" };
const AUDIO_STATUS_ICONS = {
  sent: (
    <Icon
      name="unread-message"
      aria-label={AUDIO_PLAYER_LABELS.sent}
      style={AUDIO_STATUS_ICON_SIZE}
    />
  ),
  read: (
    <Icon
      name="read-message"
      aria-label={AUDIO_PLAYER_LABELS.read}
      style={AUDIO_STATUS_ICON_SIZE}
      className={styles.audioReadIcon}
    />
  )
};

const audioMediaOf = (message: ChatMessage) =>
  message.attachment?.media?.find(
    (media) =>
      media.type === "audio" || Boolean(media.mime_type?.startsWith("audio/"))
  );

export interface SelectedMessageInfo {
  id: string;
  position: "left" | "right";
}

interface MessageListProps {
  messages: ChatMessage[];
  currentUserId: string;
  isLoading?: boolean;
  onLoadMore?: () => void;
  hasMore?: boolean;
  chatId?: string;
  onDeleteMessage?: (messageId: string) => void;
  onReportUser?: (messageId: string) => void;
  onProposePrice?: (messageId: string) => void;
  onRefusePrivateMedia?: (messageId: string) => void;
  onSendMedia?: (messageId: string) => void;
  onSelectionChange?: (selectedMessage: SelectedMessageInfo | null) => void;
  clearSelectionTrigger?: number;
  footerSlot?: React.ReactNode;
}

export const MessageList: React.FC<MessageListProps> = ({
  messages,
  currentUserId,
  isLoading = false,
  onLoadMore,
  hasMore = false,
  chatId,
  onDeleteMessage,
  onReportUser,
  onProposePrice,
  onRefusePrivateMedia,
  onSendMedia,
  onSelectionChange,
  clearSelectionTrigger,
  footerSlot
}) => {
  const { t, i18n } = useTranslation();
  const isMobile = useIsMobile();
  const [selectedMessage, setSelectedMessage] =
    useState<SelectedMessageInfo | null>(null);
  const [mediaSliderState, setMediaSliderState] = useState<{
    items: MediaSliderItem[];
    initialIndex: number;
    tagLabel?: string;
  } | null>(null);

  const sliderVideoUrls = useMemo(
    () =>
      mediaSliderState?.items
        .filter((item) => item.type === "video")
        .map((item) => item.url) ?? [],
    [mediaSliderState]
  );
  useCloudFrontCookies(
    sliderVideoUrls.length > 0 ? sliderVideoUrls : undefined
  );

  const handleMediaClick = useCallback(
    (imageId: string, images: MediaOfferImage[], tagLabel?: string) => {
      const items: MediaSliderItem[] = images
        .filter((img) => !!img.url)
        .map((img) => ({
          id: img.id,
          type: VIDEO_TYPES.includes(img.type ?? "")
            ? ("video" as const)
            : ("image" as const),
          url: img.url,
          thumbnailUrl: img.thumbnail_url
        }));
      if (items.length === 0) return;
      const index = items.findIndex((item) => item.id === imageId);
      setMediaSliderState({
        items,
        initialIndex: Math.max(0, index),
        tagLabel
      });
    },
    []
  );

  const renderSliderVideo = useCallback(
    (item: MediaSliderItem) => (
      <ReactPlayer
        key={item.id}
        url={item.url}
        controls
        playsInline
        width="100%"
        height="100%"
        config={hlsPlayerConfig}
        style={{ maxHeight: "70vh" }}
      />
    ),
    []
  );

  useEffect(() => {
    onSelectionChange?.(selectedMessage);
  }, [selectedMessage, onSelectionChange]);

  // Clear selection when trigger changes
  useEffect(() => {
    if (clearSelectionTrigger !== undefined && clearSelectionTrigger > 0) {
      setSelectedMessage(null);
    }
  }, [clearSelectionTrigger]);

  const { containerRef, messagesEndRef, messagesStartRef } =
    useMessageListScroll({
      messages,
      isLoading,
      currentUserId,
      chatId,
      onLoadMore,
      hasMore
    });

  const handleMobileBlur = useCallback(() => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  }, []);

  const handleActionIconClick = useCallback(
    (messageId: string, position: "left" | "right") => {
      setSelectedMessage((prev) =>
        prev?.id === messageId ? null : { id: messageId, position }
      );
      requestAnimationFrame(() => {
        containerRef.current
          ?.querySelector(`[data-message-id="${messageId}"]`)
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
      });
    },
    [containerRef]
  );

  const handleDeleteClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (selectedMessage && onDeleteMessage) {
        onDeleteMessage(selectedMessage.id);
      }
    },
    [selectedMessage, onDeleteMessage]
  );

  const handleReportClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (selectedMessage && onReportUser) {
        onReportUser(selectedMessage.id);
        setSelectedMessage(null);
      }
    },
    [selectedMessage, onReportUser]
  );

  // Group messages by date and prepare for rendering
  const groupedMessages = useMemo(() => {
    const groups: Array<{
      dateKey: string;
      dateLabel: string;
      messages: Array<{
        id: string;
        position: "left" | "right";
        status: "sending" | "sent" | "failed";
        readStatus: "read" | "unread" | undefined;
        timeLabel: string;
        content: string;
        message: ChatMessage;
        isDeleted: boolean;
        mediaOffer: ReturnType<typeof buildMediaOffer>;
        liveOffer: ReturnType<typeof buildLiveOffer>;
        privateMediaRequestStatus: string | undefined;
        audioMedia: ReturnType<typeof audioMediaOf>;
      }>;
    }> = [];

    let currentDateKey: string | null = null;
    let currentGroup: (typeof groups)[0] | null = null;

    messages.forEach((message) => {
      const messageId = message.id;

      const messageDate = new Date(message.date);
      const dateKey = `${messageDate.getFullYear()}-${messageDate.getMonth()}-${messageDate.getDate()}`;

      if (dateKey !== currentDateKey) {
        // New date group
        currentDateKey = dateKey;
        currentGroup = {
          dateKey,
          dateLabel: formatMessageDateSeparator(message.date, i18n.language),
          messages: []
        };
        groups.push(currentGroup);
      }

      const isCurrentUser = message.sender_id === currentUserId;
      const isDeliveredPrivateMedia =
        message.type === "private_media" &&
        message.attachment?.status === ATTACHMENT_STATUS.delivered;
      const position: "left" | "right" = isDeliveredPrivateMedia
        ? "right"
        : isCurrentUser
        ? "right"
        : "left";
      const status = getMessageStatus(message, position);
      const readStatus = isDeliveredPrivateMedia
        ? message.read_at
          ? "read"
          : "unread"
        : getReadStatus(message, currentUserId);
      const timeLabel = formatMessageTime(message.date, i18n.language);

      let content = replaceEmojiPlaceholders(message.content || "");
      if (message.status === "deleted") {
        const deletedDate = message.deleted_at
          ? new Date(message.deleted_at)
          : new Date();
        const formattedDate = new Intl.DateTimeFormat(i18n.language, {
          day: "2-digit",
          month: "2-digit",
          year: "numeric"
        }).format(deletedDate);
        const formattedTime = new Intl.DateTimeFormat(i18n.language, {
          hour: "2-digit",
          minute: "2-digit"
        }).format(deletedDate);
        content = t(
          "creators_messaging_chat_delete_message_for_everyone_label",
          `Supprimé pour tous le ${formattedDate} à ${formattedTime}`,
          { date: formattedDate, time: formattedTime }
        );
      } else if (message.type === "live" && message.attachment?.duration) {
        const price = message.attachment.price;
        const priceAmount =
          typeof price === "number" ? price : price?.amount ?? 0;
        content = t("creators_messaging_live_show_proposal_message")
          .replace("%duration%", String(message.attachment.duration / 60))
          .replace("%price%", String(priceAmount / 100));
      } else if (message.type === "live") {
        content = t("creators_messaging_live_show_proposal_message");
      }

      const mediaOffer = buildMediaOffer(message, t);
      const liveOffer = buildLiveOffer(message, t);

      currentGroup!.messages.push({
        id: messageId,
        position,
        status,
        readStatus,
        timeLabel,
        content,
        message,
        isDeleted: message.status === "deleted",
        mediaOffer,
        liveOffer,
        audioMedia: audioMediaOf(message),
        privateMediaRequestStatus:
          message.type === "private_media"
            ? (message.attachment?.status as string | undefined)
            : undefined
      });
    });
    return groups;
  }, [messages, currentUserId, i18n.language]);

  if (messages.length === 0 && !isLoading) {
    return (
      <div className={styles.emptyState} data-testid="message-list-empty">
        {t("creator_chat_noMessages")}
      </div>
    );
  }

  return (
    <div className={styles.messageListWrapper}>
      <div
        ref={containerRef}
        className={clsx(
          styles.messageList,
          selectedMessage && styles.messageListWithSelection
        )}
        onTouchStart={isMobile ? handleMobileBlur : undefined}
        data-testid="message-list"
      >
        <div ref={messagesStartRef} />
        {groupedMessages.map((group) => (
          <React.Fragment key={group.dateKey}>
            <div
              className={styles.dateSeparator}
              data-testid={`date-separator-${group.dateKey}`}
            >
              <div className={styles.dateSeparatorLine} />
              <Text className={styles.dateSeparatorText}>
                {group.dateLabel}
              </Text>
              <div className={styles.dateSeparatorLine} />
            </div>
            {group.messages.map((bubble) => {
              const isSelected = selectedMessage?.id === bubble.id;
              return (
                <div
                  key={bubble.id}
                  className={clsx(
                    bubble.position === "right"
                      ? styles.messageRowRight
                      : styles.messageRowLeft,
                    bubble.privateMediaRequestStatus &&
                      bubble.privateMediaRequestStatus !==
                        ATTACHMENT_STATUS.delivered &&
                      styles.messageRowWide,
                    isSelected && styles.messageRowSelected
                  )}
                  data-message-id={bubble.id}
                >
                  {bubble.audioMedia?.url ? (
                    <AudioMessagePlayer
                      source={bubble.audioMedia.url}
                      duration={
                        bubble.audioMedia.duration ??
                        bubble.message.attachment?.duration
                      }
                      sentAtLabel={bubble.timeLabel}
                      status={
                        bubble.message.sender_id === currentUserId
                          ? bubble.message.read_at
                            ? "read"
                            : "sent"
                          : bubble.message.read_at
                          ? undefined
                          : "received-unread"
                      }
                      labels={AUDIO_PLAYER_LABELS}
                      statusIcons={AUDIO_STATUS_ICONS}
                      className={clsx(
                        styles.audioMessage,
                        bubble.position === "right"
                          ? styles.audioMessageRight
                          : styles.audioMessageLeft
                      )}
                    />
                  ) : bubble.privateMediaRequestStatus ===
                    ATTACHMENT_STATUS.pending ? (
                    <MessageBubble
                      position={bubble.position}
                      status={bubble.status}
                      timeLabel={bubble.timeLabel}
                      readStatus={bubble.readStatus}
                      messageLabel={messageLabelsType["private_media"]}
                      data-testid={`private-media-request-pending-${bubble.id}`}
                    >
                      <Text variant="caption" style={{ color: "inherit" }}>
                        {bubble.content}
                      </Text>
                      <MessageRequestActions
                        status="pending"
                        primaryActionText={t(
                          "creator_chat_private_media_request_accept"
                        )}
                        secondaryActionText={t(
                          "creator_chat_private_media_request_refuse"
                        )}
                        onPrimaryAction={() => onProposePrice?.(bubble.id)}
                        onSecondaryAction={() =>
                          onRefusePrivateMedia?.(bubble.id)
                        }
                      />
                    </MessageBubble>
                  ) : bubble.privateMediaRequestStatus ===
                      ATTACHMENT_STATUS.refused ||
                    bubble.privateMediaRequestStatus ===
                      ATTACHMENT_STATUS.refused_by_creator ? (
                    <MediaOfferCard
                      description={bubble.content}
                      status="refused"
                      price={1}
                      isPaid={false}
                      position={bubble.position}
                      timeLabel={bubble.timeLabel}
                      messageLabel={messageLabelsType["private_media"]}
                      readStatus={bubble.readStatus}
                      hidePriceBadge
                    />
                  ) : bubble.privateMediaRequestStatus ===
                      ATTACHMENT_STATUS.refused_by_fan ||
                    (bubble.privateMediaRequestStatus ===
                      ATTACHMENT_STATUS.waiting_for_payment &&
                      !bubble.mediaOffer?.images?.length &&
                      !bubble.mediaOffer?.teaser) ? (
                    <MediaOfferCard
                      description={bubble.content}
                      status={
                        bubble.privateMediaRequestStatus as
                          | "refused_by_fan"
                          | "waiting_for_payment"
                      }
                      price={
                        bubble.privateMediaRequestStatus ===
                        ATTACHMENT_STATUS.refused_by_fan
                          ? 1
                          : bubble.mediaOffer?.price
                      }
                      isPaid={false}
                      position={bubble.position}
                      timeLabel={bubble.timeLabel}
                      messageLabel={messageLabelsType["private_media"]}
                      readStatus={bubble.readStatus}
                      quotedDescription
                      hidePriceBadge
                      statusBadgeLabel={
                        bubble.privateMediaRequestStatus ===
                        ATTACHMENT_STATUS.waiting_for_payment
                          ? t(
                              "creators_messaging_chat_private_media_status_waiting_for_payment"
                            )
                          : undefined
                      }
                      headerSlot={
                        <div
                          className={mediaOfferCardStyles.liveOfferContainer}
                        >
                          {bubble.privateMediaRequestStatus !==
                            ATTACHMENT_STATUS.refused_by_fan && (
                            <div className={mediaOfferCardStyles.liveOfferRow}>
                              <Text
                                className={mediaOfferCardStyles.liveOfferLabel}
                              >
                                {t(
                                  "creators_messaging_private_media_proposal_price_title"
                                )}
                              </Text>
                              <Text
                                className={mediaOfferCardStyles.liveOfferValue}
                              >
                                {bubble.mediaOffer?.formattedPrice ??
                                  t(
                                    "creators_messaging_private_media_price_free_label"
                                  )}
                              </Text>
                            </div>
                          )}
                          <div className={mediaOfferCardStyles.liveOfferRow}>
                            <Text
                              className={mediaOfferCardStyles.liveOfferLabel}
                            >
                              {t(
                                "creators_messaging_chat_private_media_status_title"
                              )}
                            </Text>
                            <Text
                              className={mediaOfferCardStyles.liveOfferValue}
                            >
                              {bubble.privateMediaRequestStatus ===
                              ATTACHMENT_STATUS.refused_by_fan
                                ? t(
                                    "creators_messaging_chat_private_media_status_refused"
                                  )
                                : t(
                                    "creator_chat_private_media_request_status_waiting"
                                  )}
                            </Text>
                          </div>
                        </div>
                      }
                    />
                  ) : bubble.privateMediaRequestStatus ===
                      ATTACHMENT_STATUS.to_deliver ||
                    bubble.privateMediaRequestStatus ===
                      ATTACHMENT_STATUS.paid ? (
                    <MediaOfferCard
                      description={bubble.content}
                      price={bubble.mediaOffer?.price}
                      status={
                        bubble.privateMediaRequestStatus as
                          | "to_deliver"
                          | "paid"
                      }
                      isPaid={false}
                      position={bubble.position}
                      timeLabel={bubble.timeLabel}
                      messageLabel={messageLabelsType["private_media"]}
                      readStatus={bubble.readStatus}
                      statusBadgeLabel={t(
                        "creators_messaging_chat_header_deliver_cta",
                        "To deliver"
                      )}
                      quotedDescription
                      hidePriceBadge
                      headerSlot={
                        <div
                          className={mediaOfferCardStyles.liveOfferContainer}
                        >
                          <div className={mediaOfferCardStyles.liveOfferRow}>
                            <Text
                              className={mediaOfferCardStyles.liveOfferLabel}
                            >
                              {t(
                                "creator_chat_private_media_request_paid_label"
                              )}
                            </Text>
                            <Text
                              className={mediaOfferCardStyles.liveOfferValue}
                            >
                              {bubble.mediaOffer?.formattedPrice}
                            </Text>
                          </div>
                          <div className={mediaOfferCardStyles.liveOfferRow}>
                            <Text
                              className={mediaOfferCardStyles.liveOfferLabel}
                            >
                              {t(
                                "creators_messaging_chat_private_media_status_title"
                              )}
                            </Text>
                            <Text
                              className={mediaOfferCardStyles.liveOfferValue}
                            >
                              {t(
                                "creator_chat_private_media_request_to_deliver"
                              )}
                            </Text>
                          </div>
                          <div className={styles.paidWarning}>
                            <Icon
                              name="chrono"
                              size="small"
                              className={styles.paidWarningIcon}
                            />
                            <Text className={styles.paidWarningText}>
                              {t(
                                "creator_chat_private_media_request_paid_warning"
                              )}
                            </Text>
                          </div>
                          <button
                            type="button"
                            className={mediaOfferCardStyles.liveJoinButton}
                            onClick={() => onSendMedia?.(bubble.id)}
                            data-testid="message-send-media-button"
                          >
                            {t("creator_chat_private_media_request_send_media")}
                          </button>
                        </div>
                      }
                    />
                  ) : bubble.mediaOffer && !bubble.message.attachment ? (
                    <Skeleton
                      width="305px"
                      height="380px"
                      className={styles.mediaOfferSkeleton}
                    />
                  ) : bubble.mediaOffer ? (
                    <MediaOfferCard
                      description={bubble.content}
                      price={bubble.mediaOffer.price}
                      formattedPrice={
                        bubble.mediaOffer.price === 0
                          ? t(
                              "creators_messaging_private_media_price_free_label"
                            )
                          : bubble.mediaOffer.formattedPrice
                      }
                      currency={bubble.mediaOffer.currency}
                      status={bubble.mediaOffer.status}
                      isPaid={bubble.mediaOffer.isPaid}
                      position={bubble.position}
                      images={bubble.mediaOffer.images}
                      teaser={bubble.mediaOffer.teaser}
                      mediaCounts={bubble.mediaOffer.mediaCounts}
                      timeLabel={bubble.timeLabel}
                      messageLabel={
                        messageLabelsType[
                          bubble.mediaOffer.messageTypeLabel ?? ""
                        ] ?? bubble.mediaOffer.messageTypeLabel
                      }
                      readStatus={bubble.readStatus}
                      onImageClick={(imageId) =>
                        handleMediaClick(
                          imageId,
                          [
                            ...(bubble.mediaOffer!.images ?? []),
                            ...(bubble.mediaOffer!.teaser
                              ? [bubble.mediaOffer!.teaser]
                              : [])
                          ],
                          messageLabelsType[
                            bubble.mediaOffer!.messageTypeLabel ?? ""
                          ] ?? bubble.mediaOffer!.messageTypeLabel
                        )
                      }
                      promoText={bubble.mediaOffer.promoText}
                      mentions={bubble.message.mentions}
                      liveOffer={
                        bubble.message.type === "live" && bubble.liveOffer
                          ? {
                              duration: bubble.liveOffer.duration,
                              formattedPrice:
                                bubble.liveOffer.pricing?.formatted ?? "",
                              bookingUrl: bubble.liveOffer.bookingUrl,
                              onJoin: bubble.liveOffer.bookingUrl
                                ? () => {
                                    window.location.href =
                                      bubble.liveOffer!.bookingUrl!;
                                  }
                                : undefined
                            }
                          : undefined
                      }
                      quotedDescription={
                        bubble.privateMediaRequestStatus ===
                        ATTACHMENT_STATUS.delivered
                      }
                      hidePriceBadge={
                        bubble.privateMediaRequestStatus ===
                        ATTACHMENT_STATUS.delivered
                      }
                      headerSlot={
                        bubble.privateMediaRequestStatus ===
                        ATTACHMENT_STATUS.delivered ? (
                          <div className={styles.deliveredHeaderSlot}>
                            <Text className={styles.deliveredAcceptedLabel}>
                              {t(
                                "creator_chat_private_media_request_paid_label"
                              )}
                            </Text>
                            <Text className={styles.deliveredPrice}>
                              {bubble.mediaOffer.formattedPrice}
                            </Text>
                          </div>
                        ) : undefined
                      }
                    />
                  ) : (
                    <MessageBubble
                      position={bubble.position}
                      status={bubble.status}
                      timeLabel={bubble.timeLabel}
                      readStatus={bubble.readStatus}
                      isDeleted={bubble.isDeleted}
                      variant={
                        messageVariantsType[bubble.message.type] ?? "default"
                      }
                      messageLabel={
                        messageLabelsType[bubble.message.type] ??
                        (bubble.message.type === "live"
                          ? t("creators_messaging_live_show_proposal_label")
                          : undefined)
                      }
                      mediaOffer={bubble.liveOffer}
                      actionIcon={
                        bubble.message.type === "text" && !bubble.isDeleted ? (
                          <button
                            className={styles.actionIconButton}
                            onClick={() =>
                              handleActionIconClick(bubble.id, bubble.position)
                            }
                            aria-label="Message actions"
                            data-testid={`message-action-menu-${bubble.id}`}
                          >
                            <Icon name="dot-vertical-menu" size="small" />
                          </button>
                        ) : undefined
                      }
                    >
                      {bubble.isDeleted ? (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "4px",
                            opacity: 0.7,
                            fontStyle: "italic"
                          }}
                        >
                          <Text
                            variant="caption"
                            style={{
                              color: "inherit",
                              display: "flex",
                              alignItems: "center",
                              gap: "6px"
                            }}
                          >
                            <Icon name="delete" size="small" />
                            {bubble.content}
                          </Text>
                          <Text
                            variant="caption"
                            style={{
                              color: "inherit",
                              display: "flex",
                              alignItems: "center",
                              gap: "6px"
                            }}
                          >
                            <Icon name="visibility-on" size="small" />
                            {t(
                              "creators_messaging_chat_delete_message_hidden_for_fan",
                              "Masqué pour le fan"
                            )}
                          </Text>
                        </div>
                      ) : (
                        <Text variant="caption" style={{ color: "inherit" }}>
                          <MentionText
                            content={bubble.content}
                            mentions={bubble.message.mentions}
                          />
                        </Text>
                      )}
                    </MessageBubble>
                  )}
                  {isSelected && (
                    <div
                      className={clsx(
                        styles.deleteButtonWrapper,
                        bubble.position === "right"
                          ? styles.deleteButtonWrapperRight
                          : styles.deleteButtonWrapperLeft
                      )}
                    >
                      {bubble.position === "right" ? (
                        <button
                          className={styles.deleteButton}
                          onClick={handleDeleteClick}
                          data-testid="message-delete-button"
                        >
                          <Icon name="delete" size="small" />
                          {t("creators_messaging_chat_delete_message_button")}
                        </button>
                      ) : (
                        <button
                          className={styles.reportButton}
                          onClick={handleReportClick}
                          data-testid="message-report-button"
                        >
                          <Icon name="warning" size="small" />
                          {t("creators_messaging_chat_report_fan")}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </React.Fragment>
        ))}
        {footerSlot}
        <div ref={messagesEndRef} />
      </div>
      {mediaSliderState && (
        <MediaSlider
          isOpen
          items={mediaSliderState.items}
          initialIndex={mediaSliderState.initialIndex}
          tagLabel={mediaSliderState.tagLabel}
          onClose={() => setMediaSliderState(null)}
        />
      )}
    </div>
  );
};

export default MessageList;
